<template>
  <section class="partners-section" v-if="partners.length > 0">
    <div class="section-header">
      <div class="title-area">
        <span class="section-icon partners">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
          </svg>
        </span>
        <h2 class="section-title">파트너스</h2>
        <span class="partner-count">{{ partners.length }}</span>
      </div>
      <router-link to="/partners" class="more-link">
        더보기
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M9 18l6-6-6-6"/>
        </svg>
      </router-link>
    </div>

    <!-- 로딩 -->
    <div v-if="loading" class="loading-state">
      <div class="loading-grid">
        <div v-for="i in 8" :key="i" class="skeleton-card">
          <div class="skeleton-logo"></div>
          <div class="skeleton-content">
            <div class="skeleton-title"></div>
            <div class="skeleton-badge"></div>
            <div class="skeleton-text"></div>
          </div>
        </div>
      </div>
    </div>

    <template v-else>
      <!-- 데스크톱: 그리드 -->
      <div class="partners-grid">
        <router-link 
          v-for="partner in partners" 
          :key="partner.id" 
          to="/partners"
          class="partner-card"
        >
          <div class="partner-logo-wrapper">
            <img 
              :src="partner.logo" 
              :alt="partner.name"
              class="partner-logo"
            />
          </div>
          
          <div class="partner-info">
            <h3 class="partner-name">{{ partner.name }}</h3>
            <span class="partner-category">{{ partner.category }}</span>
            
            <p class="partner-description" v-if="partner.description">
              {{ partner.description }}
            </p>
            
            <div class="partner-contact">
              <a 
                v-if="partner.phone" 
                :href="`tel:${partner.phone}`" 
                class="contact-item phone"
                @click.stop
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                </svg>
                {{ partner.phone }}
              </a>
              
              <a 
                v-if="partner.website" 
                :href="partner.website" 
                target="_blank"
                rel="noopener noreferrer"
                class="contact-item website"
                @click.stop
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                </svg>
                웹사이트
              </a>
            </div>
          </div>
        </router-link>
      </div>

      <!-- 모바일: 슬라이드 -->
      <div class="partners-slider" ref="sliderRef" @scroll="handleScroll">
        <div class="slider-track">
          <router-link 
            v-for="partner in partners" 
            :key="'mobile-' + partner.id" 
            to="/partners"
            class="partner-card"
          >
            <div class="partner-logo-wrapper">
              <img 
                :src="partner.logo" 
                :alt="partner.name"
                class="partner-logo"
              />
            </div>
            
            <div class="partner-info">
              <h3 class="partner-name">{{ partner.name }}</h3>
              <span class="partner-category">{{ partner.category }}</span>
              
              <p class="partner-description" v-if="partner.description">
                {{ partner.description }}
              </p>
            </div>
          </router-link>
        </div>
      </div>

      <!-- 모바일 인디케이터 -->
      <div v-if="totalPages > 1" class="slider-indicators">
        <button 
          v-for="index in totalPages" 
          :key="index"
          class="indicator"
          :class="{ active: currentPage === index - 1 }"
          @click="scrollToPage(index - 1)"
        />
      </div>
    </template>
  </section>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { usePartners } from '@/composables/usePartners'

export default {
  name: 'PartnersSection',
  setup() {
    const { getPartners, loading } = usePartners()
    const partners = ref([])
    const sliderRef = ref(null)
    const currentPage = ref(0)
    const cardWidth = 252 // 카드 너비 240px + gap 12px

    // 총 페이지 수 계산 (한 화면에 약 1.5개 카드 표시)
    const totalPages = computed(() => {
      return Math.ceil(partners.value.length / 1.5)
    })

    // 스크롤 이벤트 핸들러
    const handleScroll = () => {
      if (sliderRef.value) {
        const scrollLeft = sliderRef.value.scrollLeft
        const page = Math.round(scrollLeft / (cardWidth * 1.5))
        currentPage.value = Math.min(page, totalPages.value - 1)
      }
    }

    // 특정 페이지로 스크롤
    const scrollToPage = (page) => {
      if (sliderRef.value) {
        sliderRef.value.scrollTo({
          left: page * cardWidth * 1.5,
          behavior: 'smooth'
        })
      }
    }

    onMounted(async () => {
      try {
        partners.value = await getPartners(8) // 8개만 표시
      } catch (e) {
        console.error('파트너 로드 실패:', e)
      }
    })

    return {
      partners,
      loading,
      sliderRef,
      currentPage,
      totalPages,
      handleScroll,
      scrollToPage
    }
  }
}
</script>

<style scoped>
.partners-section {
  padding: 48px 0;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  padding: 0 24px;
}

.title-area {
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-icon {
  display: flex;
}

.section-icon.partners {
  color: #3182f6;
}

.section-title {
  font-size: 22px;
  font-weight: 700;
  color: #191f28;
  letter-spacing: -0.5px;
}

.partner-count {
  padding: 4px 10px;
  background: #dbeafe;
  color: #3182f6;
  font-size: 13px;
  font-weight: 600;
  border-radius: 12px;
}

.more-link {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  font-weight: 500;
  color: #8b95a1;
  transition: color 0.15s;
}

.more-link:hover {
  color: #3182f6;
}

/* 로딩 스켈레톤 */
.loading-state {
  padding: 0 24px;
}

.loading-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
}

.skeleton-card {
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  border: 1px solid #f2f4f6;
}

.skeleton-logo {
  width: 100%;
  aspect-ratio: 2 / 1;
  background: linear-gradient(90deg, #f2f4f6 25%, #e5e8eb 50%, #f2f4f6 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 12px;
  margin-bottom: 16px;
}

.skeleton-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.skeleton-title {
  width: 70%;
  height: 20px;
  background: linear-gradient(90deg, #f2f4f6 25%, #e5e8eb 50%, #f2f4f6 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-badge {
  width: 40%;
  height: 16px;
  background: linear-gradient(90deg, #f2f4f6 25%, #e5e8eb 50%, #f2f4f6 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-text {
  width: 90%;
  height: 14px;
  background: linear-gradient(90deg, #f2f4f6 25%, #e5e8eb 50%, #f2f4f6 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
  margin-top: 8px;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

/* 데스크톱: 그리드 */
.partners-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  padding: 0 24px;
}

/* 모바일: 슬라이드 (기본 숨김) */
.partners-slider {
  display: none;
}

.slider-indicators {
  display: none;
}

.partner-card {
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  border: 1px solid #f2f4f6;
  transition: all 0.2s;
  cursor: pointer;
}

.partner-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  border-color: #e5e8eb;
}

/* 로고 (2:1 비율) */
.partner-logo-wrapper {
  width: 100%;
  aspect-ratio: 2 / 1;
  background: transparent;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.partner-logo {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

/* 파트너 정보 */
.partner-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.partner-name {
  font-size: 16px;
  font-weight: 600;
  color: #191f28;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.partner-category {
  display: inline-block;
  padding: 4px 12px;
  background: #f2f4f6;
  color: #4e5968;
  font-size: 12px;
  font-weight: 500;
  border-radius: 6px;
  width: fit-content;
}

.partner-description {
  font-size: 13px;
  color: #6b7684;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin-top: 4px;
}

/* 연락처 */
.partner-contact {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
  padding-top: 12px;
  border-top: 1px solid #f2f4f6;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #4e5968;
  transition: color 0.15s;
}

.contact-item:hover {
  color: #3182f6;
}

.contact-item svg {
  flex-shrink: 0;
}

.contact-item.phone {
  color: #3182f6;
}

.contact-item.website {
  color: #6b7684;
}

/* 태블릿 */
@media (max-width: 1200px) {
  .partners-grid {
    grid-template-columns: repeat(3, 1fr);
  }

  .loading-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

/* 모바일 */
@media (max-width: 768px) {
  .partners-section {
    padding: 32px 0;
  }

  .section-header {
    padding: 0 16px;
  }

  .section-title {
    font-size: 20px;
  }

  /* 데스크톱 그리드 숨김 */
  .partners-grid {
    display: none;
  }

  /* 로딩 상태 */
  .loading-state {
    padding: 0 16px;
  }

  .loading-grid {
    display: flex;
    gap: 12px;
    overflow: hidden;
  }

  .skeleton-card {
    flex: 0 0 240px;
    padding: 16px;
  }

  /* 모바일 슬라이더 */
  .partners-slider {
    display: block;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    -ms-overflow-style: none;
    scroll-snap-type: x mandatory;
  }

  .partners-slider::-webkit-scrollbar {
    display: none;
  }

  .slider-track {
    display: flex;
    gap: 12px;
    padding: 0 16px;
  }

  .slider-track .partner-card {
    flex: 0 0 240px;
    min-width: 240px;
    padding: 16px;
    scroll-snap-align: start;
  }

  .slider-track .partner-card:hover {
    transform: none;
  }

  .slider-track .partner-logo-wrapper {
    padding: 12px;
    margin-bottom: 12px;
  }

  .slider-track .partner-name {
    font-size: 15px;
  }

  .slider-track .partner-category {
    font-size: 11px;
    padding: 3px 10px;
  }

  .slider-track .partner-description {
    font-size: 12px;
    -webkit-line-clamp: 2;
    margin-top: 6px;
  }

  .slider-track .partner-contact {
    display: none;
  }

  /* 인디케이터 */
  .slider-indicators {
    display: flex;
    justify-content: center;
    gap: 8px;
    padding-top: 16px;
  }

  .indicator {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #d1d5db;
    border: none;
    cursor: pointer;
    transition: all 0.2s;
    padding: 0;
  }

  .indicator.active {
    width: 24px;
    border-radius: 4px;
    background: #3182f6;
  }
}
</style>